import React, { useState, useEffect } from 'react';
import { Risk } from '../types';
import { BowtieDiagram } from './bowtie.types';
import { BowtieCanvas } from './BowtieCanvas';
import { riskService } from '../riskService';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface BowtieTabProps {
  risks: Risk[];
  initialRiskId?: string;
  onDiagramUpdated?: () => void;
}

export const BowtieTab: React.FC<BowtieTabProps> = ({ risks, initialRiskId, onDiagramUpdated }) => {
  const [selectedRiskId, setSelectedRiskId] = useState<string>(
    initialRiskId || (risks.length > 0 ? risks[0].id : '')
  );
  const [diagram, setDiagram] = useState<BowtieDiagram | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDiagram = async (id: string) => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await riskService.getBowtieDiagram(id);
      setDiagram(data);
    } catch (err: any) {
      console.error('Failed to load Bowtie diagram:', err);
      setError(err.message || 'Failed to load Bowtie diagram');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRiskId) {
      loadDiagram(selectedRiskId);
    }
  }, [selectedRiskId]);

  useEffect(() => {
    if (!selectedRiskId && risks.length > 0) {
      setSelectedRiskId(risks[0].id);
    }
  }, [risks]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Risk Selector Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#ffffff',
          padding: '14px 20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '300px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
            Select Risk Assessment:
          </label>
          <select
            value={selectedRiskId}
            onChange={(e) => setSelectedRiskId(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              fontWeight: 500,
              background: '#ffffff',
              color: '#0f172a',
              flex: 1,
              maxWidth: '500px',
            }}
          >
            {risks.map((r) => (
              <option key={r.id} value={r.id}>
                {r.riskNumber} — {r.title} ({r.initialRiskIndex} {r.initialTolerability})
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => loadDiagram(selectedRiskId)}
          disabled={loading || !selectedRiskId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            background: '#ffffff',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            color: '#475569',
          }}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          Reload Diagram
        </button>
      </div>

      {/* Main Canvas or Error State */}
      {loading && !diagram ? (
        <div style={{ minHeight: '700px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0' }} />
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fef2f2', borderRadius: '16px', border: '1px solid #fecaca', color: '#b91c1c' }}>
          <AlertCircle size={32} style={{ margin: '0 auto 12px auto', display: 'block' }} />
          <p style={{ fontSize: '14px', fontWeight: 600 }}>{error}</p>
        </div>
      ) : diagram ? (
        <BowtieCanvas
          diagram={diagram}
          onDiagramUpdated={(updated) => {
            setDiagram(updated);
            if (onDiagramUpdated) onDiagramUpdated();
          }}
          height="700px"
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', color: '#64748b' }}>
          <p>No risk selected or available for Bowtie visualization.</p>
        </div>
      )}
    </div>
  );
};
